import { useEffect, useState } from "react";
import axios from "axios";
import ImageModal from "./ImageModal";
import { API_URL } from "../config";

function Dashboard() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const handleImageClick = (url) => setSelectedImage(url);

  const [booking, setBooking] = useState([]);

  const filteredTrips = booking.filter(booking => booking.tribname?.toLowerCase().includes(searchTerm.toLowerCase()));
  const totalPeople = filteredTrips.reduce((sum, book) => sum + Number(book.people || 0), 0);

  useEffect(() => { fetchBooking(); }, []);

  const fetchBooking = async () => {
    try {
      const res = await axios.get(`${API_URL}/booking`);
      setBooking(res.data);
    }
    catch (err) {
      console.error("error", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) return;
    try {
      await axios.delete(`${API_URL}/booking/${id}`);
      fetchBooking();
    }
    catch (err) {
      console.error("error 2 ", err)
    }
  };

  return (
    <div className="p-6">
      {/* Header Card */}
      <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 mb-6 border border-white/20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-white flex items-center gap-3">
              <span className="text-4xl">🚤</span> Trip Bookings
            </h2>
            <p className="text-cyan-200 mt-1">Manage all diving trip reservations</p>
          </div>
          
          {/* Stats Cards */}
          <div className="flex gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl px-5 py-3 text-white">
              <p className="text-sm opacity-80">Total Bookings</p>
              <p className="text-2xl font-bold">{filteredTrips.length}</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl px-5 py-3 text-white">
              <p className="text-sm opacity-80">Total People</p>
              <p className="text-2xl font-bold">{totalPeople}</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mt-6 relative">
          <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-gray-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
          <input
            type="text"
            placeholder="Search by trip name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-96 pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl 
              text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-400"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/10 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/20">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-blue-600 to-cyan-600">
                <th className="px-4 py-4 text-left text-white font-semibold">Trip Name</th>
                <th className="px-4 py-4 text-left text-white font-semibold">First Name</th>
                <th className="px-4 py-4 text-left text-white font-semibold">Last Name</th>
                <th className="px-4 py-4 text-left text-white font-semibold">Phone</th>
                <th className="px-4 py-4 text-left text-white font-semibold">People</th>
                <th className="px-4 py-4 text-left text-white font-semibold">Passport</th>
                <th className="px-4 py-4 text-left text-white font-semibold">Date</th>
                <th className="px-4 py-4 text-left text-white font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrips.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-white/50">
                    <div className="text-5xl mb-3">🔍</div>
                    <p>No bookings found</p>
                  </td>
                </tr>
              ) : (
                filteredTrips.map((booking, index) => (
                  <tr key={booking.id} className={`border-b border-white/10 hover:bg-white/5 transition-colors
                    ${index % 2 === 0 ? 'bg-white/5' : ''}`}>
                    <td className="px-4 py-3 text-white font-medium">{booking.tribname}</td>
                    <td className="px-4 py-3 text-white/80">{booking.firstname}</td>
                    <td className="px-4 py-3 text-white/80">{booking.lastname}</td>
                    <td className="px-4 py-3 text-cyan-300">{booking.phone}</td>
                    <td className="px-4 py-3">
                      <span className="bg-blue-500/30 text-blue-200 px-3 py-1 rounded-full text-sm">
                        {booking.people}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {booking.passport ? (
                        <img 
                          src={booking.passport} 
                          alt="passport"
                          onClick={() => handleImageClick(booking.passport)}
                          className="w-12 h-12 object-cover rounded-lg cursor-pointer hover:scale-110 
                            transition-transform border-2 border-white/20"
                        />
                      ) : (
                        <span className="text-white/40 text-sm">No image</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white/80">{booking.date}</td>
                    <td className="px-4 py-3">
                      <button 
                        onClick={() => handleDelete(booking.id)} 
                        className="bg-red-500/20 hover:bg-red-500 text-red-300 hover:text-white 
                          px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedImage && (
        <ImageModal imageUrl={selectedImage} onClose={() => setSelectedImage(null)} />
      )}
    </div>
  );
}

export default Dashboard;